import React, { useEffect, useState } from 'react';
import axios from 'axios';

// Simple admin page for managing products
export default function AdminProducts() {
  const [authed, setAuthed] = useState(false);
  const [keyInput, setKeyInput] = useState('');
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState({
    name: '',
    description: '',
    price: '',
    images: '', // comma separated
    category: '',
    subcategory: '',
    compatibleModels: '',
    yearRange: ''
  });
  const [editId, setEditId] = useState(null);

  // Read admin key from storage and set axios header
  useEffect(() => {
    const saved = localStorage.getItem('bmwplug-admin-key');
    if (saved) {
      axios.defaults.headers.common['x-admin-key'] = saved;
      // attempt verify
      axios.post('/api/admin/verify').then(() => setAuthed(true)).catch(() => {
        delete axios.defaults.headers.common['x-admin-key'];
        localStorage.removeItem('bmwplug-admin-key');
      });
    }
  }, []);

  // Fetch products from backend (public)
  useEffect(() => {
    axios.get('/api/products').then(res => setProducts(res.data));
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      axios.defaults.headers.common['x-admin-key'] = keyInput;
      await axios.post('/api/admin/verify');
      localStorage.setItem('bmwplug-admin-key', keyInput);
      setAuthed(true);
    } catch (err) {
      delete axios.defaults.headers.common['x-admin-key'];
      const status = err?.response?.status;
      if (status === 503) {
        alert('Admin er ikke konfigurert på serveren (mangler ADMIN_SECRET).');
      } else if (status === 401) {
        alert('Feil passord');
      } else {
        alert('Kunne ikke logge inn. Prøv igjen senere.');
      }
    }
  };

  // Handle form input
  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Add or update product
  const handleSubmit = async e => {
    e.preventDefault();
    if (!authed) {
      alert('Du må logge inn som admin for å gjøre endringer');
      return;
    }
    const product = {
      ...form,
      price: parseFloat(form.price),
      compatibleModels: form.compatibleModels.split(',').map(m => m.trim()),
      images: form.images.split(',').map(url => url.trim()).filter(Boolean)
    };
    if (editId) {
      // Update product via backend
      const res = await axios.put(`/api/products/${editId}`, product);
      setProducts(products.map(p => p.id === editId ? res.data : p));
      setEditId(null);
    } else {
      // Add product via backend
      const res = await axios.post('/api/products', product);
      setProducts([...products, res.data]);
    }
    setForm({ name: '', description: '', price: '', images: '', category: '', subcategory: '', compatibleModels: '', yearRange: '' });
  };

  // Edit product
  const handleEdit = p => {
    setEditId(p.id);
    setForm({
      name: p.name,
      description: p.description,
      price: p.price,
      images: Array.isArray(p.images) ? p.images.join(', ') : (p.images || ''),
      category: p.category,
      subcategory: p.subcategory,
      compatibleModels: Array.isArray(p.compatibleModels) ? p.compatibleModels.join(', ') : (p.compatibleModels || ''),
      yearRange: p.yearRange
    });
  };

  // Delete product
  const handleDelete = async id => {
    if (!authed) {
      alert('Du må logge inn som admin for å gjøre endringer');
      return;
    }
    await axios.delete(`/api/products/${id}`);
    setProducts(products.filter(p => p.id !== id));
    if (editId === id) setEditId(null);
  };

  if (!authed) {
    return (
      <div className="admin-login-wrapper">
        <div className="admin-login-card">
          <div className="admin-login-icon" aria-hidden>🔒</div>
          <h2 className="admin-title">Administrasjon</h2>
          <p className="admin-subtitle">Skriv inn adminnøkkelen for å administrere produkter.</p>
          <form onSubmit={handleLogin} className="admin-form">
            <input
              className="admin-input"
              type="password"
              placeholder="Admin passord"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              required
            />
            <div className="form-actions">
              <button type="submit" className="admin-btn admin-btn-primary">Logg inn</button>
            </div>
            <p className="admin-hint">Har du glemt nøkkelen? Ta kontakt med systemansvarlig.</p>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-products-container">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 className="admin-title" style={{ marginBottom: 0 }}>Administrer produkter</h2>
          <button
            type="button"
            className="admin-btn admin-btn-secondary"
            onClick={() => {
              setAuthed(false);
              setKeyInput('');
              delete axios.defaults.headers.common['x-admin-key'];
              localStorage.removeItem('bmwplug-admin-key');
            }}
          >
            Logg ut
          </button>
        </div>
      <form className="admin-form" onSubmit={handleSubmit}>
        <div className="form-row">
          <input name="name" placeholder="Produktnavn" value={form.name} onChange={handleChange} required />
          <input name="price" type="number" step="0.01" placeholder="Pris i kr" value={form.price} onChange={handleChange} required />
        </div>
        <div className="form-row">
          <input name="category" placeholder="Kategori" value={form.category} onChange={handleChange} required />
          <input name="subcategory" placeholder="Underkategori" value={form.subcategory} onChange={handleChange} />
        </div>
        <div className="form-row">
          <input name="compatibleModels" placeholder="Kompatible modeller (kommaseparert)" value={form.compatibleModels} onChange={handleChange} />
          <input name="yearRange" placeholder="Årsmodeller" value={form.yearRange} onChange={handleChange} />
        </div>
        <textarea name="description" placeholder="Beskrivelse" value={form.description} onChange={handleChange} required rows={2} />
  <input name="images" placeholder="Bildeadresser (kommaseparert)" value={form.images} onChange={handleChange} />
        <div className="form-actions">
          <button type="submit" className="admin-btn admin-btn-primary">{editId ? 'Oppdater' : 'Legg til'} produkt</button>
          {editId && <button type="button" className="admin-btn admin-btn-secondary" onClick={() => { setEditId(null); setForm({ name: '', description: '', price: '', images: '', category: '', subcategory: '', compatibleModels: '', yearRange: '' }); }}>Avbryt</button>}
        </div>
      </form>
      <table className="admin-table">
        <thead>
          <tr>
            <th>Navn</th>
            <th>Pris</th>
            <th>Kategori</th>
            <th>Underkategori</th>
            <th>Bilder</th>
            <th>Handlinger</th>
          </tr>
        </thead>
        <tbody>
          {products.map(p => (
            <tr key={p.id}>
              <td>{p.name}</td>
              <td>{p.price}</td>
              <td>{p.category}</td>
              <td>{p.subcategory}</td>
              <td>
                {Array.isArray(p.images) && p.images.length > 0 ? (
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {p.images.map((img, i) => (
                      <img key={i} src={img} alt="product" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, border: '1px solid #eee' }} />
                    ))}
                  </div>
                ) : (
                  <span style={{ color: '#bbb' }}>Ingen bilder</span>
                )}
              </td>
              <td>
                <button className="admin-btn admin-btn-edit" onClick={() => handleEdit(p)}>Rediger</button>
                <button className="admin-btn admin-btn-delete" onClick={() => handleDelete(p.id)} style={{ marginLeft: 8 }}>Slett</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
